/* iter146 — Connecteam-style payroll-first Timesheets.
   - Weekly view: Employee | Issues | Sat..Fri | Total | Breaks | Paid | Status
   - Employee search/filter (Daily + Weekly)
   - Issue badges: missing_clockout, auto_closed, over_12h, over_16h, no_break_6h, pending_edit
   - Dark-mode readable typography (no tiny gray text on payroll values)
   - Snap-to-Saturday on the date picker (iter145, preserved)
*/
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Clock, Users, ChevronLeft, ChevronRight, AlertTriangle,
  AlertOctagon, CircleAlert, Search, CheckCircle2, Download, Pencil, Plus, FileText, ShieldAlert, Lock, LockOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import AdminTimeclockEditSheet from '../../components/AdminTimeclockEditSheet';
import TimesheetNotesDrawer from '../../components/TimesheetNotesDrawer';
import TimesheetDayDetailDrawer from '../../components/TimesheetDayDetailDrawer';
import TimesheetReconcileDialog from '../../components/TimesheetReconcileDialog';
import EditRequestsTab from '../../components/EditRequestsTab';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { fmtLocalTime } from '../../lib/timeFmt';
// iter165 — Use the company-tz formatter so timesheet times match the schedule.
const fmtTime = (d) => d ? fmtLocalTime(d, { hour: '2-digit', minute: '2-digit' }) : '—';

// iter144 — Shift the selected date by ±N days (used by Prev/Next Week buttons).
const shiftDate = (iso, days) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// iter145 — Snap any date to the Saturday that starts its payroll week.
const snapToSaturday = (iso) => {
  if (!iso) return iso;
  const d = new Date(`${iso}T00:00:00`);
  const back = (d.getDay() + 1) % 7;
  if (back === 0) return iso;
  d.setDate(d.getDate() - back);
  return d.toISOString().slice(0, 10);
};

// iter146 — Render a single ISO date as a compact column header ("Sat 6/20").
const fmtCol = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
    return `${dow} ${d.getMonth() + 1}/${d.getDate()}`;
  } catch { return iso; }
};

// iter147 — Quote a single CSV cell. Doubles inner quotes per RFC 4180 and
// wraps the value in quotes if it contains a comma, quote, or newline.
const csvCell = (val) => {
  const s = val == null ? '' : String(val);
  if (s === '') return '';
  const needsQuote = /[",\n\r]/.test(s);
  const inner = s.replace(/"/g, '""');
  return needsQuote ? `"${inner}"` : inner;
};

// iter147 — Build a Connecteam-style payroll CSV from the loaded weekly data,
// honoring the active employee-search filter the dispatcher set. Columns match
// the on-screen grid plus a flattened Issues column so the file is QuickBooks
// payroll-ready without further hand-editing.
const buildPayrollCsv = ({ data, rows }) => {
  const dayAxis = data?.day_axis || [];
  const headerDays = dayAxis.map(fmtCol);
  const header = [
    'Employee',
    ...headerDays,
    'Total Hours',
    'Breaks',
    'Paid Hours',
    'Status',
    'Issues',
  ];
  const lines = [header.map(csvCell).join(',')];
  rows.forEach((r) => {
    const dayCells = dayAxis.map((d) => (Number((r.by_day || {})[d] || 0)).toFixed(2));
    lines.push([
      r.user_name || '',
      ...dayCells,
      (Number(r.total_hours) || 0).toFixed(2),
      (Number(r.break_hours) || 0).toFixed(2),
      (Number(r.paid_hours) || 0).toFixed(2),
      (r.status || 'ok').toUpperCase(),
      (r.issues || []).join(' | '),
    ].map(csvCell).join(','));
  });
  return lines.join('\r\n');
};

// iter146 — One canonical place that maps an issue code to its badge styling +
// human label. Order here = display order (worst payroll concern first).
const ISSUE_META = {
  missing_clockout: { label: 'Missing Clock-Out', cls: 'bg-red-500/15 border-red-500/50 text-red-300',     Icon: AlertOctagon },
  auto_closed:      { label: 'Auto-Closed',       cls: 'bg-red-500/15 border-red-500/50 text-red-300',     Icon: AlertOctagon },
  over_16h:         { label: 'Over 16h',          cls: 'bg-red-500/15 border-red-500/50 text-red-300',     Icon: AlertOctagon },
  over_12h:         { label: 'Over 12h',          cls: 'bg-amber-500/15 border-amber-500/50 text-amber-300', Icon: AlertTriangle },
  no_break_6h:      { label: 'No Break',          cls: 'bg-amber-500/15 border-amber-500/50 text-amber-300', Icon: AlertTriangle },
  pending_edit:     { label: 'Edit Pending',      cls: 'bg-blue-500/15 border-blue-500/50 text-blue-300',   Icon: CircleAlert },
  // iter158 — Phase 4: field tech clocked in without a scheduled shift
  fallback_clockin: { label: 'Fallback Clock-In', cls: 'bg-amber-500/15 border-amber-500/50 text-amber-300', Icon: AlertTriangle },
  // iter218 — active employee with no time entries for the period
  no_entries:       { label: 'No Entries',        cls: 'bg-amber-500/15 border-amber-500/50 text-amber-300', Icon: AlertTriangle },
};

const IssueBadge = ({ code }) => {
  const meta = ISSUE_META[code];
  if (!meta) return null;
  const { Icon } = meta;
  return (
    <span data-testid={`issue-${code}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
};

// iter146 — Render the payroll Status pill. Color follows worst-case severity.
const StatusPill = ({ status }) => {
  const map = {
    needs_edit:   { label: 'Needs Edit',  cls: 'bg-red-500/20 border-red-500/60 text-red-200' },
    pending_edit: { label: 'Edit Pending', cls: 'bg-blue-500/20 border-blue-500/60 text-blue-200' },
    auto_closed:  { label: 'Auto-Closed', cls: 'bg-red-500/20 border-red-500/60 text-red-200' },
    no_entries:   { label: 'No Entries',  cls: 'bg-amber-500/20 border-amber-500/60 text-amber-200' },
    missing_time: { label: 'Missing Time', cls: 'bg-amber-500/20 border-amber-500/60 text-amber-200' },
    ok:           { label: 'OK',          cls: 'bg-green-500/15 border-green-500/50 text-green-300' },
  };
  const meta = map[status] || map.ok;
  return (
    <span data-testid={`status-${status || 'ok'}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>
      {status === 'ok' ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {meta.label}
    </span>
  );
};

// iter233 — Live "Currently Clocked In" board. Statuses: Clocked In / On Break /
// Missing Clock-Out (only after the auto-close rule fires). Elapsed ticks locally.
const fmtElapsedFrom = (iso, nowMs) => {
  if (!iso) return '';
  const start = new Date(iso).getTime();
  let mins = Math.max(0, Math.round((nowMs - start) / 60000));
  const h = Math.floor(mins / 60); const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const LIVE_STATUS = {
  clocked_in:       { label: 'Clocked In',       cls: 'bg-green-500/15 border-green-500/50 text-green-300' },
  on_break:         { label: 'On Break',         cls: 'bg-amber-500/15 border-amber-500/50 text-amber-300' },
  missing_clockout: { label: 'Missing Clock-Out', cls: 'bg-red-500/15 border-red-500/60 text-red-300' },
};

const LiveClockedIn = ({ live, nowMs, onOpenDay }) => {
  const rows = live?.rows || [];
  const counts = live?.counts || { clocked_in: 0, on_break: 0, missing_clockout: 0 };
  return (
    <div data-testid="currently-clocked-in" className="bg-[#141414] border border-[#262626] rounded-lg p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-white font-bold flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
          </span>
          Currently Clocked In
          <span className="text-[#A3A3A3] text-sm font-normal" data-testid="live-count">({live?.count || 0})</span>
        </h3>
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
          <span className="px-2 py-0.5 rounded border bg-green-500/15 border-green-500/50 text-green-300" data-testid="live-count-clockedin">{counts.clocked_in} Clocked In</span>
          <span className="px-2 py-0.5 rounded border bg-amber-500/15 border-amber-500/50 text-amber-300" data-testid="live-count-onbreak">{counts.on_break} On Break</span>
          {counts.missing_clockout > 0 && (
            <span className="px-2 py-0.5 rounded border bg-red-500/15 border-red-500/60 text-red-300" data-testid="live-count-missing">{counts.missing_clockout} Missing Clock-Out</span>
          )}
          <span className="text-[#525252] normal-case font-normal">auto-refresh 30s</span>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-[#A3A3A3] text-sm py-4 text-center" data-testid="live-empty">No one is clocked in right now.</div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => {
            const meta = LIVE_STATUS[r.status] || LIVE_STATUS.clocked_in;
            return (
              <div key={r.entry_id} data-testid={`live-row-${r.user_id}`} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white font-bold truncate">{r.user_name}</span>
                  <span data-testid={`live-status-${r.user_id}`} className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>{meta.label}</span>
                </div>
                <div className="mt-1 text-xs text-[#C9C9C9] space-y-0.5">
                  <div>In {r.clock_in_et} · <span className="text-white font-semibold" data-testid={`live-elapsed-${r.user_id}`}>{fmtElapsedFrom(r.clock_in, nowMs)}</span> on clock</div>
                  {r.on_break && (
                    <div className="text-amber-300" data-testid={`live-break-${r.user_id}`}>Break since {r.break_start_et} · {fmtElapsedFrom(r.break_start, nowMs)}</div>
                  )}
                  <div className="text-[#A3A3A3]">{r.current_segment_label}{r.current_job ? ` · ${r.current_job}` : ''}</div>
                  <div className="text-[#525252]">Last activity {r.last_activity_et}</div>
                </div>
                <div className="mt-2">
                  <button
                    type="button"
                    data-testid={`live-open-day-${r.user_id}`}
                    onClick={() => onOpenDay(r.user_id, r.user_name)}
                    className="text-[11px] font-bold text-[#D4AF37] hover:underline"
                  >View / manage entry →</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// iter233 — Read-only raw-record audit + evidence-based correction preview, with a
// Super-Admin-only explicit apply. Proves the Troy/Austin diagnostics in preview and
// is the tool run against production after deploy.
const CONF_CLS = {
  high: 'text-red-300 border-red-500/50 bg-red-500/10',
  medium: 'text-amber-300 border-amber-500/50 bg-amber-500/10',
  low: 'text-blue-300 border-blue-500/50 bg-blue-500/10',
  info: 'text-[#A3A3A3] border-[#404040] bg-[#1a1a1a]',
};

const AuditCorrectionsPanel = ({ employeesList, isSuperAdmin, onApplied }) => {
  const [employeeId, setEmployeeId] = useState('');
  const [start, setStart] = useState('2026-07-25');
  const [end, setEnd] = useState('2026-07-31');
  const [audit, setAudit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applyRow, setApplyRow] = useState(null);
  const [applyReason, setApplyReason] = useState('');
  const [applying, setApplying] = useState(false);

  const run = async () => {
    setLoading(true); setAudit(null); setPreview(null);
    try {
      const qs = `?${employeeId ? `employee_id=${employeeId}&` : ''}start=${start}&end=${end}`;
      const [a, p] = await Promise.all([
        axios.get(`${API}/admin/timesheets/audit${qs}`, { withCredentials: true }),
        axios.get(`${API}/admin/timesheets/correction-preview${qs}`, { withCredentials: true }),
      ]);
      setAudit(a.data); setPreview(p.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Audit failed');
    }
    setLoading(false);
  };

  const applyCorrection = async () => {
    if ((applyReason || '').trim().length < 5) { toast.error('A reason of at least 5 characters is required.'); return; }
    setApplying(true);
    try {
      await axios.post(`${API}/admin/timesheets/apply-correction`, {
        entry_id: applyRow.entry_id,
        correction_type: applyRow.kind,
        proposed_clock_in: applyRow.proposed_clock_in,
        proposed_clock_out: applyRow.proposed_clock_out,
        reason: applyReason.trim(),
        evidence: applyRow.evidence,
      }, { withCredentials: true });
      toast.success('Correction applied and audited.');
      setApplyRow(null); setApplyReason('');
      onApplied && onApplied();
      run();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    }
    setApplying(false);
  };

  const f = audit?.findings || {};
  return (
    <div data-testid="audit-corrections-panel" className="space-y-4">
      <div className="bg-[#141414] border border-[#262626] rounded-lg p-4">
        <p className="text-[#A3A3A3] text-sm mb-3">Read-only raw-record audit and evidence-based correction preview. Nothing changes until a Super Admin explicitly applies a correction.</p>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="block text-xs text-[#A3A3A3] mb-1">Employee</label>
            <select data-testid="audit-employee" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
              className="bg-[#0A0A0A] border border-[#262626] text-white rounded-md px-3 py-2 text-sm min-w-[200px]">
              <option value="">All employees</option>
              {(employeesList || []).map((e) => <option key={e.id || e._id} value={e.id || e._id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-[#A3A3A3] mb-1">Start (ET)</label>
            <Input data-testid="audit-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white w-[150px]" />
          </div>
          <div>
            <label className="block text-xs text-[#A3A3A3] mb-1">End (ET)</label>
            <Input data-testid="audit-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white w-[150px]" />
          </div>
          <Button data-testid="audit-run" onClick={run} disabled={loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#c19f2e]">
            <Search className="w-4 h-4 mr-1" /> {loading ? 'Running…' : 'Run Audit'}
          </Button>
        </div>
      </div>

      {audit && (
        <div className="bg-[#141414] border border-[#262626] rounded-lg p-4" data-testid="audit-findings">
          <h4 className="text-white font-bold mb-2">Findings</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            {[
              ['TZ day-shifts', f.tz_day_shifts], ['Open entries', f.open_entries],
              ['Auto-closed', f.auto_closed], ['Voided', f.voided],
              ['Orphan segments', f.orphan_segments], ['Segments outside parent', f.segments_outside_parent],
              ['Identity mismatches', f.identity_mismatches], ['Stored mismatches', f.stored_mismatches],
            ].map(([l, v]) => (
              <div key={l} className="bg-[#0A0A0A] border border-[#262626] rounded p-2">
                <div className={`text-lg font-bold ${v ? 'text-amber-300' : 'text-[#A3A3A3]'}`}>{v ?? 0}</div>
                <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">{l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {preview && (
        <div className="bg-[#141414] border border-[#262626] rounded-lg p-4" data-testid="correction-previews">
          <h4 className="text-white font-bold mb-2">Correction Preview ({preview.preview_count})</h4>
          {preview.preview_count === 0 ? (
            <p className="text-[#A3A3A3] text-sm" data-testid="no-corrections">No corrections needed — records are clean for this range.</p>
          ) : (
            <div className="space-y-2">
              {preview.previews.map((p, i) => (
                <div key={i} data-testid={`preview-${p.kind}-${i}`} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-white font-semibold text-sm">{p.employee_name || '—'}{p.date ? ` · ${p.date}` : ''}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${CONF_CLS[p.confidence] || CONF_CLS.info}`}>{p.kind.replace(/_/g, ' ')} · {p.confidence}</span>
                  </div>
                  <div className="mt-1 text-xs text-[#C9C9C9]"><span className="text-[#A3A3A3]">Current: </span>{p.current_value}</div>
                  <div className="text-xs text-green-300"><span className="text-[#A3A3A3]">Proposed: </span>{p.proposed_value}</div>
                  {p.affected_daily != null && <div className="text-xs text-amber-300">Impact: {p.affected_daily > 0 ? '+' : ''}{p.affected_daily}h/day</div>}
                  <div className="text-[11px] text-[#737373] mt-1">Evidence: {p.evidence}</div>
                  {(p.warnings || []).map((w, wi) => <div key={wi} className="text-[11px] text-amber-400 mt-0.5">⚠ {w}</div>)}
                  {isSuperAdmin && p.entry_id && p.proposed_clock_in && ['segment_outside_parent', 'edit_application_failed'].includes(p.kind) && (
                    <Button data-testid={`apply-${i}`} onClick={() => { setApplyRow(p); setApplyReason(''); }}
                      className="mt-2 h-7 text-xs bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#c19f2e]">
                      <ShieldAlert className="w-3 h-3 mr-1" /> Apply Correction (Super Admin)
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {audit && (
        <div className="bg-[#141414] border border-[#262626] rounded-lg p-4" data-testid="audit-records">
          <h4 className="text-white font-bold mb-2">Raw Records ({audit.record_count})</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-[#A3A3A3] text-left border-b border-[#262626]">
                <th className="py-1 pr-2">Type</th><th className="pr-2">Employee</th><th className="pr-2">ET Day</th>
                <th className="pr-2">Start→End (ET)</th><th className="pr-2">Paid</th><th className="pr-2">In Payroll</th><th>Reason / Warning</th>
              </tr></thead>
              <tbody>
                {audit.records.map((r, i) => (
                  <tr key={i} data-testid={`record-${r.record_type}-${i}`} className="border-b border-[#1a1a1a]">
                    <td className="py-1 pr-2 text-white">{r.record_type}</td>
                    <td className="pr-2 text-[#C9C9C9]">{r.user_name || '—'}</td>
                    <td className="pr-2 text-[#C9C9C9]">{r.et_day || '—'}{r.tz_day_shift ? ' ⚠tz' : ''}</td>
                    <td className="pr-2 text-[#C9C9C9]">{r.start_et || (r.start ? new Date(r.start).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }) : '—')}{r.end_et ? ` → ${r.end_et.split(' ').slice(-2).join(' ')}` : ''}</td>
                    <td className="pr-2 text-white">{r.paid_net_hours != null ? `${r.paid_net_hours}h` : '—'}</td>
                    <td className="pr-2">{r.included_in_payroll ? <span className="text-green-400">yes</span> : <span className="text-[#737373]">no</span>}</td>
                    <td className={r.integrity_warning ? 'text-amber-400' : 'text-[#737373]'}>{r.integrity_warning || r.inclusion_reason}{r.identity_mismatch ? ` · ${r.identity_mismatch}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {applyRow && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="apply-correction-dialog" onClick={() => !applying && setApplyRow(null)}>
          <div className="bg-[#141414] border border-[#262626] rounded-lg p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-white font-bold flex items-center gap-2 mb-2"><ShieldAlert className="w-4 h-4 text-[#D4AF37]" /> Apply Historical Correction</h4>
            <p className="text-[#A3A3A3] text-sm mb-1">This changes a raw timeclock record and is fully audited.</p>
            <div className="bg-[#0A0A0A] border border-[#262626] rounded p-2 text-xs text-[#C9C9C9] mb-3">
              <div><span className="text-[#A3A3A3]">Proposed: </span>{applyRow.proposed_value}</div>
              <div className="text-[#737373] mt-1">Evidence: {applyRow.evidence}</div>
            </div>
            <label className="block text-xs text-[#A3A3A3] mb-1">Reason (required, min 5 chars)</label>
            <textarea data-testid="apply-reason" rows={3} value={applyReason} onChange={(e) => setApplyReason(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-md px-3 py-2 text-sm" placeholder="Why is this correction justified by the evidence?" />
            {applyReason.length > 0 && applyReason.trim().length < 5 && (
              <p data-testid="apply-reason-error" className="text-red-400 text-xs mt-1">Reason must be at least 5 characters.</p>
            )}
            <div className="flex justify-end gap-2 mt-3">
              <Button data-testid="apply-cancel" variant="outline" onClick={() => setApplyRow(null)} disabled={applying} className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
              <Button data-testid="apply-confirm" onClick={applyCorrection} disabled={applying} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#c19f2e]">{applying ? 'Applying…' : 'Apply & Audit'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



const AdminTimesheets = () => {
  const { hasPermission, user } = useAuth();
  const canEdit = hasPermission('edit_timesheets');
  const canApprove = hasPermission('approve_time_edits');
  // iter194 — Top-level tabs: Timesheets grid vs Edit Requests queue.
  const [topTab, setTopTab] = useState('timesheets');
  // iter194 — Pending-request count for the tab badge.
  const [pendingReqCount, setPendingReqCount] = useState(0);
  // iter145 — Initial date snaps to the containing Saturday so the default
  // WEEKLY view picker visibly aligns with the payroll-week start.
  const [date, setDate] = useState(snapToSaturday(new Date().toISOString().slice(0, 10)));
  const [view, setView] = useState('weekly');
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // iter146 — Employee search applies to BOTH daily + weekly. Stored as a free-text
  // string and matched client-side against `user_name` so the API doesn't have to
  // know about partial-match logic. Server-side filtering by exact employee_id is
  // also supported but not used by the search box.
  const [search, setSearch] = useState('');
  // iter158-cleanup — Phase 4 drill-down: which weekly rows are expanded to show
  // per-job breakdown beneath the row. Keyed by user_id.
  const [expandedRows, setExpandedRows] = useState({});
  // iter170 — Admin Timesheet Edit + audit sheet state.
  const [editSheet, setEditSheet] = useState({ open: false, entry: null, mode: 'edit' });
  const [employeesList, setEmployeesList] = useState([]);
  // iter233 — Live "Currently Clocked In" board (30s poll + 1s local ticker).
  const [live, setLive] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  // iter247 — Payroll week lock state + dialogs.
  const [lockStatus, setLockStatus] = useState(null);
  const [lockDialog, setLockDialog] = useState(false);
  const [unlockDialog, setUnlockDialog] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [lockBusy, setLockBusy] = useState(false);
  const fetchLive = React.useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/timesheets/currently-clocked-in`, { withCredentials: true });
      setLive(d);
    } catch { /* keep last */ }
  }, []);
  useEffect(() => {
    fetchLive();
    const poll = setInterval(fetchLive, 30000);       // 30s server refresh
    const tick = setInterval(() => setNowTick(Date.now()), 1000); // 1s local elapsed ticker
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [fetchLive]);

  // iter170 — Load employees once for the create-entry picker.
  useEffect(() => {
    if (!canEdit) return;
    axios.get(`${API}/admin/employees`, { withCredentials: true })
      .then(({ data: d }) => setEmployeesList(Array.isArray(d) ? d : []))
      .catch(() => setEmployeesList([]));
  }, [canEdit]);

  const openEditEntry = (entry) => setEditSheet({ open: true, entry, mode: 'edit' });
  const openCreateEntry = (prefill = null) => setEditSheet({ open: true, entry: prefill, mode: 'create' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [date, view]);

  const refresh = async () => {
    setData(null);
    setError(null);
    setLoading(true);
    try {
      if (view === 'daily') {
        const { data: d } = await axios.get(`${API}/admin/timesheets/daily?date=${date}`, { withCredentials: true });
        setData(d);
      } else {
        const { data: d } = await axios.get(`${API}/admin/timesheets/weekly?week_start=${date}`, { withCredentials: true });
        setData(d);
        // iter247 — payroll lock status + reconciliation for this week
        try {
          const { data: ls } = await axios.get(`${API}/admin/payroll/lock-status?week_start=${date}`, { withCredentials: true });
          setLockStatus(ls);
        } catch { setLockStatus(null); }
      }
      // iter194 — Track only pending count. Full listing lives in the Edit Requests tab.
      try {
        const e = await axios.get(`${API}/admin/time-edits?status=pending`, { withCredentials: true });
        const arr = Array.isArray(e.data) ? e.data : [];
        setEdits(arr);
        setPendingReqCount(arr.length);
      } catch { setEdits([]); setPendingReqCount(0); }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load timesheets';
      setError(typeof msg === 'string' ? msg : 'Failed to load timesheets');
    }
    setLoading(false);
  };

  // iter194 — Old inline approve/reject helper removed. All decision logic now
  // lives inside <EditRequestsTab />.

  // iter194 — Notes drawer context. Kept as a small state machine so the drawer
  // can re-derive its note list from freshly-fetched `data` after a review.
  //   mode:'employee'    → notes = row.notes for user_id
  //   mode:'unreviewed'  → notes = union of all unreviewed notes this week
  const [notesCtx, setNotesCtx] = useState({ open: false, mode: null, userId: null });
  // iter199 Phase A1 — read-only diagnostics UI state.
  const [dayDrawer, setDayDrawer] = useState({ open: false, employeeId: null, employeeName: '', date: null });
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [tsReload, setTsReload] = useState(0);
  const afterTimesheetSave = () => { refresh(); setTsReload((n) => n + 1); fetchLive(); };
  // iter247 — Lock / unlock payroll week handlers.
  const isLocked = !!lockStatus?.locked;
  const recon = lockStatus?.reconciliation;
  const doLockWeek = async (override) => {
    setLockBusy(true);
    try {
      await axios.post(`${API}/admin/payroll/lock`, { week_start: date, override_warnings: !!override }, { withCredentials: true });
      toast.success(`Payroll week ${date} locked`);
      setLockDialog(false);
      await refresh();
    } catch (err) {
      const d = (!Array.isArray(err?.response?.data?._detailMeta) && err?.response?.data?._detailMeta) || err?.response?.data?.detail;
      if (d?.code === 'UNRESOLVED_ITEMS') {
        // Keep dialog open; warnings already shown. User can override.
        toast.error('Unresolved items — review or lock anyway.');
      } else {
        toast.error((typeof d === 'string' ? d : d?.message) || 'Lock failed');
      }
    }
    setLockBusy(false);
  };
  const doUnlockWeek = async () => {
    if ((unlockReason || '').trim().length < 5) { toast.error('Enter a reason (min 5 chars).'); return; }
    setLockBusy(true);
    try {
      await axios.post(`${API}/admin/payroll/unlock`, { week_start: date, reason: unlockReason.trim() }, { withCredentials: true });
      toast.success(`Payroll week ${date} unlocked`);
      setUnlockDialog(false); setUnlockReason('');
      await refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Unlock failed');
    }
    setLockBusy(false);
  };
  const openDayDetail = (employeeId, employeeName, dateStr) => setDayDrawer({ open: true, employeeId, employeeName, date: dateStr });
  const openNotesForEmployee = (row) => {
    setNotesCtx({ open: true, mode: 'employee', userId: row.user_id, title: `${row.user_name || 'Employee'} — Notes` });
  };
  const openUnreviewedNotes = () => {
    setNotesCtx({ open: true, mode: 'unreviewed', userId: null, title: `Unreviewed Notes — Wk ${data?.week_start || ''}` });
  };
  const closeNotes = () => setNotesCtx({ open: false, mode: null, userId: null });
  const drawerNotes = useMemo(() => {
    if (!notesCtx.open || !data) return [];
    if (notesCtx.mode === 'employee') {
      const rows = data.by_employee || data.summary || [];
      const row = rows.find((r) => r.user_id === notesCtx.userId);
      return row?.notes || [];
    }
    if (notesCtx.mode === 'unreviewed') {
      return (data.summary || []).flatMap((s) => (s.notes || []).filter((n) => !n.reviewed_at));
    }
    return [];
  }, [notesCtx, data]);
  const refreshNotesInDrawer = () => { refresh(); };

  // iter147 — Build and trigger a Payroll CSV download. Uses the already-loaded
  // weekly response + the active employee-search filter (weeklyRows below) so the
  // file always matches what the dispatcher is reading on screen. No backend
  // round-trip required.
  const exportPayrollCsv = () => {
    try {
      if (!data || !weeklyRows.length) {
        toast.error('Nothing to export');
        return;
      }
      const csv = buildPayrollCsv({ data, rows: weeklyRows });
      // Prepend a UTF-8 BOM so Excel / Numbers open special characters cleanly.
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ws = data.week_start || date;
      const we = data.week_end || date;
      const suffix = search.trim() ? `-${search.trim().replace(/[^a-z0-9]+/gi, '_')}` : '';
      a.download = `nexflow-payroll-${ws}-to-${we}${suffix}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Exported ${weeklyRows.length} employee${weeklyRows.length > 1 ? 's' : ''}`);
    } catch (err) {
      console.error(err);
      toast.error('Export failed');
    }
  };

  // iter146 — Client-side employee filter. Empty search → show everyone.
  const matchesSearch = (name) => {
    if (!search.trim()) return true;
    return (name || '').toLowerCase().includes(search.trim().toLowerCase());
  };

  // iter146 — Day-axis derived once per render so we can sort columns Sat→Fri.
  const dayAxis = useMemo(() => (data?.day_axis || []), [data]);

  const weeklyRows = useMemo(() => {
    const rows = (data?.summary || []).filter((r) => matchesSearch(r.user_name));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search]);

  const dailyGroups = useMemo(() => {
    const rows = (data?.by_employee || []).filter((g) => matchesSearch(g.user_name));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search]);

  // iter233 — live map so the weekly grid can show a running "Clocked In / On Break"
  // state in today's cell instead of a blank/final value for open entries.
  const liveByUser = useMemo(() => {
    const m = {};
    (live?.rows || []).forEach((r) => { m[r.user_id] = r; });
    return m;
  }, [live]);
  const todayISOEt = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

  // iter146 — Payroll roll-up at the top of the weekly grid (sum across visible rows).
  const weeklyTotals = useMemo(() => {
    let total = 0, breaks = 0, paid = 0, issues = 0;
    weeklyRows.forEach((r) => {
      total += Number(r.total_hours) || 0;
      breaks += Number(r.break_hours) || 0;
      paid += Number(r.paid_hours) || 0;
      issues += Number(r.issue_count) || 0;
    });
    return { total, breaks, paid, issues, employees: weeklyRows.length };
  }, [weeklyRows]);

  return (
    <div data-testid="admin-timesheets-page" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-2">
            <Clock className="w-6 h-6 text-[#D4AF37]" /> Timesheets
          </h2>
          <p className="text-[#C9C9C9] text-sm">Payroll review · Saturday–Friday week · auto clock-out at 11:59 PM ET</p>
        </div>
      </div>

      {/* iter194 — Top-level tab strip. Timesheets grid vs Edit Requests queue. */}
      <Tabs value={topTab} onValueChange={setTopTab} className="w-full">
        <TabsList data-testid="admin-ts-tabs" className="bg-[#141414] border border-[#262626]">
          <TabsTrigger data-testid="tab-timesheets" value="timesheets" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">
            <Clock className="w-4 h-4 mr-1" /> Timesheets
          </TabsTrigger>
          <TabsTrigger data-testid="tab-edit-requests" value="edit_requests" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">
            <Pencil className="w-4 h-4 mr-1" /> Edit Requests
            {pendingReqCount > 0 && (
              <span data-testid="edit-req-badge" className="ml-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {pendingReqCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-audit" value="audit" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">
            <Search className="w-4 h-4 mr-1" /> Audit &amp; Corrections
          </TabsTrigger>
        </TabsList>

        <TabsContent value="audit" className="mt-4">
          <AuditCorrectionsPanel employeesList={employeesList} isSuperAdmin={user?.role === 'super_admin'} onApplied={afterTimesheetSave} />
        </TabsContent>

        <TabsContent value="edit_requests" className="mt-4">
          {canApprove ? (
            <EditRequestsTab currentUserId={user?.id || user?._id} />
          ) : (
            <p className="text-[#A3A3A3] text-sm">You do not have permission to review timesheet edit requests.</p>
          )}
        </TabsContent>

        <TabsContent value="timesheets" className="mt-4 space-y-4">
          <LiveClockedIn
            live={live}
            nowMs={nowTick}
            onOpenDay={(uid, name) => openDayDetail(uid, name, new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }))}
          />
          <div className="flex gap-3 items-center flex-wrap">
        <div className="flex gap-1">
          {['daily', 'weekly'].map(v => (
            <Button
              data-testid={`view-${v}`}
              key={v}
              onClick={() => {
                if (v === 'weekly') setDate((d) => snapToSaturday(d));
                setView(v);
              }}
              variant={view === v ? 'default' : 'outline'}
              className={view === v ? 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold' : 'border-[#262626] text-white hover:bg-[#262626]'}
            >
              {v.toUpperCase()}
            </Button>
          ))}
        </div>
        <div className="flex gap-1 items-center">
          <Button data-testid="ts-prev" onClick={() => setDate(shiftDate(date, view === 'weekly' ? -7 : -1))} variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626] h-9 px-2" title={view === 'weekly' ? 'Previous week' : 'Previous day'}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Input
            data-testid="ts-date"
            type="date"
            value={date}
            onChange={(e) => {
              const picked = e.target.value;
              setDate(view === 'weekly' ? snapToSaturday(picked) : picked);
            }}
            className="bg-[#0A0A0A] border-[#262626] text-white w-40"
          />
          <Button data-testid="ts-next" onClick={() => setDate(shiftDate(date, view === 'weekly' ? 7 : 1))} variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626] h-9 px-2" title={view === 'weekly' ? 'Next week' : 'Next day'}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            data-testid="ts-today"
            onClick={() => {
              const today = new Date().toISOString().slice(0, 10);
              setDate(view === 'weekly' ? snapToSaturday(today) : today);
            }}
            variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626] h-9 px-3 text-xs" title="Jump back to today's week"
          >
            Today
          </Button>
        </div>
        {/* iter146 — Employee search/filter applies to both Daily and Weekly */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none" />
          <Input
            data-testid="ts-search"
            type="text"
            placeholder="Search employee…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-[#0A0A0A] border-[#262626] text-white pl-8 h-9"
          />
        </div>
        {/* iter170 — Admin "Add Missing Entry" button. Visible to anyone with
            edit_timesheets permission. Opens AdminTimeclockEditSheet in create mode. */}
        {canEdit && (
          <Button
            data-testid="ts-add-missing-entry"
            onClick={openCreateEntry}
            size="sm"
            className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold h-9"
            title="Add a missing clock-in/out for an employee"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Entry
          </Button>
        )}
      </div>

      {/* iter218 — Payroll roster reconciliation banner. Every active employee
          must appear; this reconciles active roster vs displayed rows. */}
      {view === 'weekly' && data?.roster_reconciliation && (
        <div
          data-testid="roster-reconciliation"
          className={`rounded-md p-3 border text-sm ${data.roster_reconciliation.missing_from_view?.length ? 'bg-red-500/10 border-red-500/50' : 'bg-[#141414] border-[#262626]'}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            {data.roster_reconciliation.missing_from_view?.length ? <AlertTriangle className="w-4 h-4 text-red-300" /> : <CheckCircle2 className="w-4 h-4 text-green-400" />}
            <span className={data.roster_reconciliation.missing_from_view?.length ? 'text-red-200' : 'text-[#A3A3A3]'}>
              <span className="font-bold text-white" data-testid="roster-active-count">{data.roster_reconciliation.active_payroll_employees}</span> active payroll employees ·{' '}
              <span className="font-bold text-white" data-testid="roster-displayed-count">{data.roster_reconciliation.displayed}</span> displayed ·{' '}
              <span className="font-bold text-white" data-testid="roster-missing-count">{data.roster_reconciliation.missing_from_view?.length || 0}</span> missing from payroll view
            </span>
          </div>
          <div className="flex gap-3 mt-1 text-[11px] text-[#525252] flex-wrap">
            <span>{data.roster_reconciliation.with_valid_entries} with valid entries</span>
            <span>{data.roster_reconciliation.with_invalid_entries} with invalid entries</span>
            <span>{data.roster_reconciliation.with_no_entries} with no entries</span>
            {data.roster_reconciliation.off_roster_with_hours > 0 && <span className="text-amber-300">{data.roster_reconciliation.off_roster_with_hours} off-roster with hours</span>}
          </div>
          {data.roster_reconciliation.missing_from_view?.length > 0 && (
            <div className="mt-1 text-red-200 text-xs" data-testid="roster-missing-list">
              Missing: {data.roster_reconciliation.missing_from_view.map((m) => `${m.user_name || m.user_id} (${m.reason})`).join(', ')}
            </div>
          )}
        </div>
      )}

      {/* iter194 — Unreviewed-notes warning banner (weekly view). Payroll can
          still close but should acknowledge notes first. */}
      {view === 'weekly' && data?.unreviewed_note_count > 0 && (
        <div data-testid="unreviewed-notes-warning" className="bg-amber-500/10 border border-amber-500/50 rounded-md p-3 flex items-center justify-between flex-wrap gap-2">
          <p className="text-amber-200 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span>
              <span className="font-bold" data-testid="unreviewed-notes-count">{data.unreviewed_note_count}</span> unreviewed note{data.unreviewed_note_count > 1 ? 's' : ''} this week — you can still close payroll, but please review them first.
            </span>
          </p>
          <Button
            data-testid="review-all-notes"
            size="sm"
            variant="outline"
            onClick={openUnreviewedNotes}
            className="border-amber-500/60 text-amber-200 hover:bg-amber-500/10"
          >
            Review notes
          </Button>
        </div>
      )}

      {/* iter194 — Old inline "Pending Edit Requests" panel removed. Requests
          now live in the "Edit Requests" tab above so approvers get filters,
          decline dialog, and audit trail context. */}

      {loading && (
        <div data-testid="ts-loading" className="text-[#C9C9C9] text-sm py-6 text-center">Loading timesheets…</div>
      )}
      {!loading && error && (
        <div data-testid="ts-error" className="bg-[#7F1D1D]/20 border border-[#7F1D1D] rounded-md p-4 text-red-200 text-sm">
          <p className="font-bold mb-1">Couldn&apos;t load timesheets</p>
          <p className="text-xs mb-3">{error}</p>
          <Button onClick={refresh} size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Retry</Button>
        </div>
      )}

      {/* Daily view */}
      {!loading && !error && view === 'daily' && data && (
        <div className="space-y-3">
          {dailyGroups.length === 0 ? (
            <p className="text-[#C9C9C9] text-center py-8">{search ? `No matches for "${search}" on ${data.date || date}` : `No time entries on ${data.date || date}`}</p>
          ) : dailyGroups.map(g => (
            <div key={g.user_id} data-testid={`daily-emp-${g.user_id}`} className="bg-[#141414] border border-[#262626] rounded-md p-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <p className="text-white font-bold text-base flex items-center gap-2"><Users className="w-4 h-4 text-[#D4AF37]" /> {g.user_name}</p>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-[#D4AF37] font-bold text-base">{(g.total_hours || 0).toFixed(2)}h</span>
                  <span className="text-[#C9C9C9]">Breaks {(g.break_hours || 0).toFixed(2)}h</span>
                  <span className="text-white font-bold">Paid {(g.paid_hours || 0).toFixed(2)}h</span>
                  {(g.total_paid_hours ?? 0) > (g.paid_hours ?? 0) && (
                    <span className="text-[#D4AF37] font-bold" data-testid={`daily-total-paid-${g.user_id}`} title="Worked + PTO + Sick + Holiday + Other">Total Paid {(g.total_paid_hours || 0).toFixed(2)}h</span>
                  )}
                  {(g.pto_hours || 0) > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-500/15 border border-sky-500/40 text-sky-300" data-testid={`daily-pto-${g.user_id}`}>PTO {(g.pto_hours).toFixed(2)}h</span>}
                  {(g.paid_sick_hours || 0) > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-500/15 border border-rose-500/40 text-rose-300" data-testid={`daily-sick-${g.user_id}`}>Sick {(g.paid_sick_hours).toFixed(2)}h</span>}
                  {(g.paid_holiday_hours || 0) > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-500/15 border border-violet-500/40 text-violet-300" data-testid={`daily-holiday-${g.user_id}`}>Holiday {(g.paid_holiday_hours).toFixed(2)}h</span>}
                  {(g.other_leave_hours || 0) > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300" data-testid={`daily-other-${g.user_id}`}>Other {(g.other_leave_hours).toFixed(2)}h</span>}
                  {(g.approved_bonus_total || 0) > 0 && <span className="text-[11px] font-bold text-green-300" data-testid={`daily-bonus-approved-${g.user_id}`}>+ ${(g.approved_bonus_total).toFixed(2)} bonus</span>}
                  {(g.pending_bonus_total || 0) > 0 && <span className="text-[10px] font-medium text-amber-300/80" data-testid={`daily-bonus-pending-${g.user_id}`}>${(g.pending_bonus_total).toFixed(2)} pending</span>}
                  {g.has_open_entry && (
                    <span data-testid={`daily-live-paid-${g.user_id}`} title="Still on the clock — live paid so far, not final until clock-out" className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider bg-green-500/15 border-green-500/50 text-green-300">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" /> Live {(g.live_paid_hours || 0).toFixed(2)}h · not final
                    </span>
                  )}
                  {g.issue_count > 0 && (
                    <span className="text-amber-300 font-bold flex items-center gap-1 text-xs">
                      <AlertTriangle className="w-3.5 h-3.5" /> {g.issue_count} issue{g.issue_count > 1 ? 's' : ''}
                    </span>
                  )}
                  {/* iter194 — Note badge (clickable → drawer). */}
                  {(g.note_count || 0) > 0 && (
                    <button
                      type="button"
                      data-testid={`daily-note-badge-${g.user_id}`}
                      onClick={() => openNotesForEmployee(g)}
                      className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded border transition-colors ${
                        (g.unreviewed_note_count || 0) > 0
                          ? 'bg-amber-500/15 border-amber-500/60 text-amber-200 hover:bg-amber-500/25'
                          : 'bg-[#262626] border-[#404040] text-[#C9C9C9] hover:bg-[#333]'
                      }`}
                      title="View timesheet notes"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      {g.unreviewed_note_count > 0 ? `${g.unreviewed_note_count}/${g.note_count}` : g.note_count} Note{g.note_count > 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-[#C9C9C9] text-xs">
                  <th className="text-left pb-1.5">In</th>
                  <th className="text-left pb-1.5">Out</th>
                  <th className="text-left pb-1.5">Job</th>
                  <th className="text-left pb-1.5">Issues</th>
                  <th className="text-right pb-1.5">Hours</th>
                  {canEdit && <th className="pb-1.5 w-10" />}
                </tr></thead>
                <tbody>
                  {(g.entries || []).map(e => (
                    <tr key={e.id} data-testid={`daily-entry-${e.id}`} className="border-t border-[#262626]">
                      <td className="py-1.5 text-white">{fmtTime(e.clock_in)}</td>
                      <td className="py-1.5 text-white">{fmtTime(e.clock_out)}</td>
                      <td className="py-1.5 text-[#C9C9C9]">{e.appointment_label || 'General'}</td>
                      <td className="py-1.5">
                        <div className="flex flex-wrap gap-1">
                          {(e.issues || []).length === 0 ? <span className="text-[#525252] text-xs">—</span> : (e.issues || []).map((iss) => <IssueBadge key={iss} code={iss} />)}
                        </div>
                      </td>
                      <td className="py-1.5 text-right text-[#D4AF37] font-bold">{(e.duration_hours ?? 0).toFixed(2)}h</td>
                      {canEdit && (
                        <td className="py-1.5 text-right">
                          <Button
                            data-testid={`edit-entry-${e.id}`}
                            variant="outline"
                            size="sm"
                            onClick={() => openEditEntry({ ...e, user_id: g.user_id, user_name: g.user_name })}
                            className="border-[#262626] text-[#D4AF37] hover:bg-[#262626] h-7 px-2"
                            title="Correct this entry"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Weekly Connecteam-style payroll grid */}
      {!loading && !error && view === 'weekly' && data && (
        <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden">
          {data.week_start && (
            <div data-testid="weekly-range-banner" className="bg-[#0A0A0A] border-b border-[#262626] px-4 py-3 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm text-[#D4AF37] uppercase tracking-wider font-bold">Payroll Week (Sat – Fri)</p>
                <p className="text-base text-white font-bold" data-testid="weekly-range-label">{data.week_start} → {data.week_end}</p>
              </div>
              {/* iter146 — Quick rollup so payroll can sanity-check totals before opening QuickBooks */}
              <div className="flex gap-4 text-sm flex-wrap items-center">
                <span className="text-[#C9C9C9]"><span className="text-white font-bold" data-testid="weekly-emp-count">{weeklyTotals.employees}</span> employees</span>
                <span className="text-[#C9C9C9]">Total <span className="text-[#D4AF37] font-bold" data-testid="weekly-grand-total">{weeklyTotals.total.toFixed(2)}h</span></span>
                <span className="text-[#C9C9C9]">Breaks <span className="text-white font-bold">{weeklyTotals.breaks.toFixed(2)}h</span></span>
                <span className="text-[#C9C9C9]">Paid <span className="text-white font-bold" data-testid="weekly-grand-paid">{weeklyTotals.paid.toFixed(2)}h</span></span>
                {weeklyTotals.issues > 0 && (
                  <span className="text-amber-300 font-bold flex items-center gap-1" data-testid="weekly-issue-count">
                    <AlertTriangle className="w-4 h-4" /> {weeklyTotals.issues} issue{weeklyTotals.issues > 1 ? 's' : ''}
                  </span>
                )}
                {/* iter147 — Export Payroll CSV. Respects the current employee-search
                    filter (weeklyRows is already filtered) and uses the loaded week. */}
                <Button
                  data-testid="ts-export-csv"
                  type="button"
                  onClick={exportPayrollCsv}
                  disabled={weeklyRows.length === 0}
                  size="sm"
                  className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold disabled:opacity-40 disabled:hover:bg-[#D4AF37] h-8 px-3"
                  title="Download a payroll-ready CSV of the current weekly view"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Export Payroll CSV
                </Button>
                <Button
                  data-testid="ts-reconcile-btn"
                  type="button"
                  onClick={() => setReconcileOpen(true)}
                  size="sm"
                  variant="outline"
                  className="border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold h-8 px-3"
                  title="Read-only: recompute correct paid hours and compare to weekly + payroll"
                >
                  <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />
                  Reconcile
                </Button>
                {/* iter247 — Payroll week lock control */}
                {isLocked ? (
                  <div className="flex items-center gap-2" data-testid="payroll-lock-locked">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] font-bold uppercase tracking-wider bg-red-500/15 border-red-500/50 text-red-300" title={`Locked by ${lockStatus?.lock?.locked_by_name || ''}`}>
                      <Lock className="w-3.5 h-3.5" /> Payroll Locked
                    </span>
                    <Button
                      data-testid="payroll-unlock-btn"
                      type="button"
                      onClick={() => { setUnlockReason(''); setUnlockDialog(true); }}
                      size="sm"
                      variant="outline"
                      className="border-red-500/50 text-red-300 hover:bg-red-500/10 font-bold h-8 px-3"
                    >
                      <LockOpen className="w-3.5 h-3.5 mr-1.5" /> Unlock
                    </Button>
                  </div>
                ) : (
                  <Button
                    data-testid="payroll-lock-btn"
                    type="button"
                    onClick={() => setLockDialog(true)}
                    size="sm"
                    className="bg-[#262626] text-white hover:bg-[#333] border border-[#404040] font-bold h-8 px-3"
                    title="Review reconciliation and lock this payroll week"
                  >
                    <Lock className="w-3.5 h-3.5 mr-1.5" /> Lock Payroll Week
                  </Button>
                )}
              </div>
            </div>
          )}
          {isLocked && (
            <div data-testid="payroll-locked-banner" className="bg-red-500/10 border-b border-red-500/30 px-4 py-2 text-[13px] text-red-200 flex items-center gap-2">
              <Lock className="w-4 h-4 shrink-0" />
              This payroll week is <b>locked</b>. Hours, PTO, holiday, sick and bonus changes are blocked until an authorized admin unlocks it.
              {lockStatus?.lock?.locked_by_name && <span className="text-red-300/70">Locked by {lockStatus.lock.locked_by_name}.</span>}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1200px]">
              <thead className="bg-[#0A0A0A] border-b border-[#262626] sticky top-0 z-20">
                <tr>
                  <th className="text-left px-4 py-3 text-[#D4AF37] text-xs uppercase tracking-wider font-bold sticky left-0 bg-[#0A0A0A] z-30">Employee</th>
                  <th className="text-left px-3 py-3 text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Issues</th>
                  {dayAxis.map((d) => (
                    <th key={d} data-testid={`col-${d}`} className="text-center px-3 py-3 text-[#D4AF37] text-xs uppercase tracking-wider font-bold whitespace-nowrap">{fmtCol(d)}</th>
                  ))}
                  <th className="text-right px-3 py-3 text-[#D4AF37] text-xs uppercase tracking-wider font-bold whitespace-nowrap">Total</th>
                  <th className="text-right px-3 py-3 text-[#D4AF37] text-xs uppercase tracking-wider font-bold whitespace-nowrap">Breaks</th>
                  <th className="text-right px-3 py-3 text-[#D4AF37] text-xs uppercase tracking-wider font-bold whitespace-nowrap">Paid</th>
                  <th className="text-right px-3 py-3 text-[#D4AF37] text-xs uppercase tracking-wider font-bold whitespace-nowrap">Payroll</th>
                  <th className="text-center px-3 py-3 text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Status</th>
                  <th className="text-center px-3 py-3 text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Notes</th>
                </tr>
              </thead>
              <tbody>
                {weeklyRows.length === 0 ? (
                  <tr><td colSpan={dayAxis.length + 8} className="text-center px-4 py-10 text-[#C9C9C9]">{search ? `No matches for "${search}" this week` : 'No time entries this week'}</td></tr>
                ) : weeklyRows.map((s) => {
                  const rowDanger = s.status === 'needs_edit' || s.status === 'auto_closed';
                  const isExpanded = !!expandedRows[s.user_id];
                  const byJob = s.by_job || {};
                  const byJobEntries = Object.entries(byJob).sort((a, b) => Number(b[1]) - Number(a[1]));
                  return (
                    <React.Fragment key={s.user_id}>
                      <tr
                        data-testid={`weekly-row-${s.user_id}`}
                        onClick={() => setExpandedRows((prev) => ({ ...prev, [s.user_id]: !prev[s.user_id] }))}
                        className={`border-b border-[#262626] last:border-0 hover:bg-[#0A0A0A]/40 transition-colors cursor-pointer ${rowDanger ? 'bg-red-500/[0.04]' : ''}`}
                      >
                        <td className="px-4 py-3 text-white font-bold text-base sticky left-0 bg-[#141414] z-10" data-testid={`weekly-name-${s.user_id}`}>
                          <span className="inline-flex items-center gap-2">
                            <span className="text-[#D4AF37] text-xs">{isExpanded ? '▾' : '▸'}</span>
                            {s.user_name}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {(() => {
                              const nbd = s.no_break_days || [];
                              // iter233 — clearer break wording: suppress the generic per-entry
                              // "No Break" badge in favor of an exact "N qualifying days" chip.
                              const otherIssues = (s.issues || []).filter((iss) => !(iss === 'no_break_6h' && nbd.length));
                              const badges = otherIssues.map((iss) => <IssueBadge key={iss} code={iss} />);
                              if (nbd.length) {
                                const label = `No break on ${nbd.length} qualifying day${nbd.length > 1 ? 's' : ''}`;
                                badges.push(
                                  <span
                                    key="no_break_days"
                                    data-testid={`no-break-days-${s.user_id}`}
                                    title={`Days ≥6h worked with no recorded break: ${nbd.join(', ')}`}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 border-amber-500/50 text-amber-300"
                                  >
                                    <AlertTriangle className="w-3 h-3" /> {label}
                                  </span>
                                );
                              }
                              return badges.length ? badges : <span className="text-[#525252] text-xs">—</span>;
                            })()}
                          </div>
                        </td>
                        {dayAxis.map((d) => {
                          const h = Number((s.by_day || {})[d] || 0);
                          // iter233 — running live state in today's cell for an open shift.
                          const liveRow = liveByUser[s.user_id];
                          const showLive = liveRow && d === todayISOEt;
                          return (
                            <td key={d} data-testid={`cell-${s.user_id}-${d}`} className="px-3 py-3 text-center" onClick={(ev) => ev.stopPropagation()}>
                              {showLive ? (
                                <button
                                  type="button"
                                  data-testid={`cell-live-${s.user_id}-${d}`}
                                  onClick={() => openDayDetail(s.user_id, s.user_name, d)}
                                  className={`inline-flex flex-col items-center min-w-[64px] px-2 py-1 rounded border cursor-pointer transition-colors ${liveRow.status === 'on_break' ? 'bg-amber-500/15 border-amber-500/50 text-amber-200 hover:bg-amber-500/25' : 'bg-green-500/15 border-green-500/50 text-green-200 hover:bg-green-500/25'}`}
                                  title="Currently on the clock — running total (not final)"
                                >
                                  <span className="text-[9px] font-bold uppercase tracking-wider">{liveRow.status === 'on_break' ? 'On Break' : 'Clocked In'}</span>
                                  <span className="text-xs font-bold">{fmtElapsedFrom(liveRow.clock_in, nowTick)}</span>
                                </button>
                              ) : h > 0 ? (
                                <button
                                  type="button"
                                  data-testid={`cell-open-${s.user_id}-${d}`}
                                  onClick={() => openDayDetail(s.user_id, s.user_name, d)}
                                  className="inline-block min-w-[52px] px-2 py-1 rounded bg-green-500/15 border border-green-500/40 text-green-200 font-bold text-base hover:bg-green-500/25 cursor-pointer transition-colors"
                                  title="Open day detail (read-only)"
                                >{h.toFixed(2)}</button>
                              ) : (
                                <span className="text-[#525252] text-base">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-3 py-3 text-right text-[#D4AF37] font-bold text-base" data-testid={`weekly-total-${s.user_id}`}>{(s.total_hours || 0).toFixed(2)}h</td>
                        <td className="px-3 py-3 text-right text-white text-base">{(s.break_hours || 0).toFixed(2)}h</td>
                        <td className="px-3 py-3 text-right text-white font-bold text-base" data-testid={`weekly-paid-${s.user_id}`}>
                          {(s.paid_hours || 0).toFixed(2)}h
                          {s.has_open_entry && (
                            <div data-testid={`weekly-live-paid-${s.user_id}`} title="Still on the clock — live paid so far, not final until clock-out" className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-green-300 flex items-center justify-end gap-1">
                              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" /> Live {(s.live_paid_hours || 0).toFixed(2)}h · not final
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right align-top" data-testid={`weekly-payroll-${s.user_id}`}>
                          <div className="text-white font-bold text-base" data-testid={`weekly-total-paid-hours-${s.user_id}`}>{(s.total_paid_hours ?? s.paid_hours ?? 0).toFixed(2)}h</div>
                          <div className="flex flex-wrap gap-1 justify-end mt-1">
                            {(s.pto_hours || 0) > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-500/15 border border-sky-500/40 text-sky-300" data-testid={`weekly-pto-${s.user_id}`}>PTO {(s.pto_hours).toFixed(2)}h</span>}
                            {(s.paid_sick_hours || 0) > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-500/15 border border-rose-500/40 text-rose-300" data-testid={`weekly-sick-${s.user_id}`}>Sick {(s.paid_sick_hours).toFixed(2)}h</span>}
                            {(s.paid_holiday_hours || 0) > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-500/15 border border-violet-500/40 text-violet-300" data-testid={`weekly-holiday-${s.user_id}`}>Holiday {(s.paid_holiday_hours).toFixed(2)}h</span>}
                            {(s.other_leave_hours || 0) > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300" data-testid={`weekly-other-${s.user_id}`}>Other {(s.other_leave_hours).toFixed(2)}h</span>}
                          </div>
                          {(s.approved_bonus_total || 0) > 0 && (
                            <div className="mt-1 text-[11px] font-bold text-green-300" data-testid={`weekly-bonus-approved-${s.user_id}`} title={`${s.approved_bonus_count || 0} approved bonus${(s.approved_bonus_count || 0) === 1 ? '' : 'es'} in this week`}>+ ${(s.approved_bonus_total).toFixed(2)} bonus</div>
                          )}
                          {(s.pending_bonus_total || 0) > 0 && (
                            <div className="mt-0.5 text-[10px] font-medium text-amber-300/80" data-testid={`weekly-bonus-pending-${s.user_id}`} title="Pending — not included in gross yet">${(s.pending_bonus_total).toFixed(2)} pending</div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center"><StatusPill status={s.status} /></td>
                        <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          {(s.note_count || 0) > 0 ? (
                            <button
                              type="button"
                              data-testid={`weekly-note-badge-${s.user_id}`}
                              onClick={() => openNotesForEmployee(s)}
                              className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded border transition-colors ${
                                (s.unreviewed_note_count || 0) > 0
                                  ? 'bg-amber-500/15 border-amber-500/60 text-amber-200 hover:bg-amber-500/25'
                                  : 'bg-[#262626] border-[#404040] text-[#C9C9C9] hover:bg-[#333]'
                              }`}
                              title="View timesheet notes"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              {s.unreviewed_note_count > 0 ? `${s.unreviewed_note_count}/${s.note_count}` : s.note_count}
                            </button>
                          ) : (
                            <span className="text-[#525252] text-base">—</span>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr data-testid={`weekly-drilldown-${s.user_id}`} className="bg-[#0A0A0A]/60">
                          <td colSpan={dayAxis.length + 8} className="px-6 py-4">
                            <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold mb-2">Where {s.user_name}&apos;s hours went</p>
                            {byJobEntries.length === 0 ? (
                              <p className="text-[#525252] text-sm">No job-tagged time this week — entries were general/fallback only.</p>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {byJobEntries.map(([jobLabel, hrs]) => {
                                  const h = Number(hrs) || 0;
                                  const pct = s.total_hours ? Math.round((h / s.total_hours) * 100) : 0;
                                  return (
                                    <div key={jobLabel} className="bg-[#141414] border border-[#262626] rounded-md p-3">
                                      <p className="text-white text-sm font-medium truncate" title={jobLabel}>{jobLabel}</p>
                                      <div className="flex items-end justify-between mt-1">
                                        <p className="text-[#D4AF37] font-bold text-lg">{h.toFixed(2)}h</p>
                                        <p className="text-[#A3A3A3] text-xs">{pct}% of week</p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* iter146 — By-Job rollups stay available below the grid for deeper payroll review */}
          {weeklyRows.some((r) => Object.keys(r.by_job || {}).length > 0) && (
            <div className="px-4 py-3 border-t border-[#262626] bg-[#0A0A0A]/50">
              <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold mb-2">By Job</p>
              <div className="space-y-1.5">
                {weeklyRows.map((r) => (
                  Object.keys(r.by_job || {}).length === 0 ? null : (
                    <p key={r.user_id} className="text-sm">
                      <span className="text-white font-bold">{r.user_name}:</span>{' '}
                      <span className="text-[#C9C9C9]">
                        {Object.entries(r.by_job || {}).map(([j, h]) => `${j}: ${(Number(h) || 0).toFixed(1)}h`).join(' • ')}
                      </span>
                    </p>
                  )
                ))}
              </div>
            </div>
          )}
        </div>
      )}
        </TabsContent>
      </Tabs>

      {/* iter170 — Admin Timesheet Edit + Audit sheet */}
      <AdminTimeclockEditSheet
        open={editSheet.open}
        onOpenChange={(o) => setEditSheet((s) => ({ ...s, open: o }))}
        entry={editSheet.entry}
        mode={editSheet.mode}
        employees={employeesList}
        onSaved={() => { afterTimesheetSave(); }}
      />

      {/* iter194 — Notes drawer (employee-level or unreviewed-week list) */}
      <TimesheetNotesDrawer
        open={notesCtx.open}
        onClose={closeNotes}
        notes={drawerNotes}
        title={notesCtx.title || 'Timesheet Notes'}
        onReviewed={refreshNotesInDrawer}
      />

      {/* iter199 Phase A1 — Read-only diagnostics: day drill-down + reconciliation */}
      <TimesheetDayDetailDrawer
        open={dayDrawer.open}
        onOpenChange={(o) => setDayDrawer((s) => ({ ...s, open: o }))}
        employeeId={dayDrawer.employeeId}
        employeeName={dayDrawer.employeeName}
        date={dayDrawer.date}
        canEdit={canEdit}
        reloadToken={tsReload}
        onEditEntry={(entry) => openEditEntry(entry)}
        onAddEntry={() => openCreateEntry({ user_id: dayDrawer.employeeId, user_name: dayDrawer.employeeName, prefill_date: dayDrawer.date })}
        onChanged={afterTimesheetSave}
      />
      <TimesheetReconcileDialog
        open={reconcileOpen}
        onOpenChange={setReconcileOpen}
        weekStart={date}
        onOpenDay={(id, name, d) => { setReconcileOpen(false); openDayDetail(id, name, d); }}
      />

      {/* iter247 — Lock Payroll Week dialog (reconciliation summary) */}
      <Dialog open={lockDialog} onOpenChange={setLockDialog}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg" data-testid="payroll-lock-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Lock className="w-5 h-5 text-[#D4AF37]" /> Lock Payroll Week</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">{date} → {lockStatus?.week_end || ''} · review before locking</DialogDescription>
          </DialogHeader>
          {recon && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                {[['Worked', recon.totals.worked_paid_hours], ['PTO', recon.totals.pto_hours], ['Paid Sick', recon.totals.paid_sick_hours], ['Holiday', recon.totals.paid_holiday_hours], ['Other Leave', recon.totals.other_leave_hours], ['Total Paid', recon.totals.total_paid_hours]].map(([l, v]) => (
                  <div key={l} className="bg-[#0A0A0A] border border-[#262626] rounded p-2"><p className="text-[9px] uppercase text-[#737373]">{l}</p><p className="text-sm font-bold text-white">{(v || 0).toFixed(2)}h</p></div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-green-500/10 border border-green-500/30 rounded p-2 text-center"><p className="text-[9px] uppercase text-green-300/80">Approved Bonus Pay</p><p className="text-base font-bold text-green-300">${(recon.totals.approved_bonus_total || 0).toFixed(2)}</p><p className="text-[9px] text-[#737373]">{recon.totals.approved_bonus_count} bonus(es)</p></div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 text-center"><p className="text-[9px] uppercase text-amber-300/80">Pending Bonuses</p><p className="text-base font-bold text-amber-300">${(recon.totals.pending_bonus_total || 0).toFixed(2)}</p><p className="text-[9px] text-[#737373]">{recon.totals.pending_bonus_count} pending</p></div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="bg-[#0A0A0A] border border-[#262626] rounded p-2"><p className="text-[9px] uppercase text-[#737373]">Open Entries</p><p className="font-bold text-white">{recon.open_entries}</p></div>
                <div className="bg-[#0A0A0A] border border-[#262626] rounded p-2"><p className="text-[9px] uppercase text-[#737373]">Missing Clock-Outs</p><p className="font-bold text-white">{recon.missing_clock_outs}</p></div>
                <div className="bg-[#0A0A0A] border border-[#262626] rounded p-2"><p className="text-[9px] uppercase text-[#737373]">Pending Edits</p><p className="font-bold text-white">{recon.pending_time_edits}</p></div>
              </div>
              {recon.has_warnings && (
                <div className="bg-amber-500/10 border border-amber-500/40 rounded-md p-3" data-testid="payroll-lock-warnings">
                  <p className="text-amber-300 font-bold text-xs uppercase tracking-wider mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Unresolved items</p>
                  <ul className="list-disc list-inside text-amber-200 text-xs space-y-0.5">
                    {recon.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setLockDialog(false)} className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
            {recon?.has_warnings ? (
              <Button data-testid="payroll-lock-override-confirm" disabled={lockBusy} onClick={() => doLockWeek(true)} className="bg-amber-500 text-[#0A0A0A] hover:bg-amber-400 font-bold">Lock Anyway</Button>
            ) : (
              <Button data-testid="payroll-lock-confirm" disabled={lockBusy} onClick={() => doLockWeek(false)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">Lock Week</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter247 — Unlock Payroll Week dialog (reason required) */}
      <Dialog open={unlockDialog} onOpenChange={setUnlockDialog}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-md" data-testid="payroll-unlock-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><LockOpen className="w-5 h-5 text-red-300" /> Unlock Payroll Week</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">{date} → {lockStatus?.week_end || ''} · this is audited</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Reason (required, min 5 chars)</label>
            <Input data-testid="payroll-unlock-reason" value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)} placeholder="e.g. correcting a missed after-hours bonus" className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUnlockDialog(false)} className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
            <Button data-testid="payroll-unlock-confirm" disabled={lockBusy || unlockReason.trim().length < 5} onClick={doUnlockWeek} className="bg-red-500 text-white hover:bg-red-400 font-bold">Unlock Week</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTimesheets;
